import React from "react";
import { useAccount } from "wagmi";
import { useAuctionManager } from "../../hooks/useAuctionManager";

/**
 * useTokenRole — decides which action the item-detail modal should offer for a
 * token, from on-chain state plus the connected wallet.
 *
 * Four outcomes:
 *   'buy'    — not ours, and the token has a live auction/listing  -> "Buy"
 *   'sell'   — ours, and not listed                              -> "Sell"
 *   'manage' — ours, and already listed                          -> "Manage"
 *   'none'   — not ours and not listed (or ownership unreadable)  -> no button
 *
 * Why read the chain instead of the backend: the backend's `auctionID` is a
 * cached mirror that goes stale (a listing can be bought or delisted without
 * the cache being invalidated), and it carries no owner at all. Staking the
 * label on cached data is how you get a "Buy" button on a token you already
 * own. `getOwnerOf` is the same read the contract itself gates listings on
 * (`require(ti.ownerOf(tokenId) == msg.sender)`).
 *
 * The read chain is the TOKEN's chain, not the wallet's — a collection can
 * live on Anvil while the wallet sits on Coston. Writes elsewhere in the modal
 * still go through the connected wallet, which is the correct split (see
 * useAuctionManager).
 */
export type TokenRole = "buy" | "sell" | "manage" | "none" | "unknown";

export type UseTokenRoleArgs = {
  /** NFT contract holding the token. Required — without it the read reverts. */
  contractAddress?: string | null;
  /** ERC-721 token id, or ERC-1155 class id for 1155 collections. */
  tokenId?: string | number | bigint | null;
  /** EVM chain id the TOKEN lives on (not the connected chain). */
  chainId?: number | null;
  /** Cached backend auction id. A hint only — never trusted on its own. */
  auctionIdHint?: number | string | null;
};

export function useTokenRole({
  contractAddress,
  tokenId,
  chainId,
  auctionIdHint,
}: UseTokenRoleArgs): { role: TokenRole; owner?: string | null; loading: boolean } {
  let account: string | undefined;
  try {
    account = useAccount().address;
  } catch {
    account = undefined;
  }

  const readChainId = chainId ?? undefined;
  const am = useAuctionManager(undefined, { chainId: readChainId });

  const [owner, setOwner] = React.useState<string | null>(null);
  const [listed, setListed] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState(false);
  // Distinguishes "read not run yet" from "read ran and found nothing", so a
  // failed read doesn't flash the wrong label while the retry is in flight.
  const [resolved, setResolved] = React.useState(false);

  const contract = contractAddress || undefined;
  const tokId = tokenId !== undefined && tokenId !== null && tokenId !== "" ? String(tokenId) : undefined;

  React.useEffect(() => {
    if (!contract || !tokId || !readChainId) {
      setOwner(null);
      setListed(false);
      setResolved(true);
      return;
    }
    let dead = false;
    setLoading(true);
    setResolved(false);

    (async () => {
      let o: string | null = null;
      try {
        o = (await am.getOwnerOf(contract, BigInt(tokId))) as string;
      } catch {
        // Unreadable ownership (wrong chain, no marketplace deployment, revoked
        // token). Report 'unknown' rather than guessing — showing "Sell" to a
        // non-owner would just produce a contract revert.
        if (!dead) { setOwner(null); setListed(false); setResolved(true); setLoading(false); }
        return;
      }
      if (dead) return;
      const ownerAddr = o ? String(o) : null;
      setOwner(ownerAddr);

      // A listing is only the current seller's to manage if the token is
      // listed at all. Two shapes, because straight sale listings are keyed by
      // an id in the shared counter while auctions are keyed by (contract,id):
      //   1) the id-keyed getSaleListingInfo, when the caller has an id;
      //   2) the token-keyed getAuctionStatus / getERC1155SaleListing,
      //      which need no id and are therefore authoritative on their own.
      let isListed = false;
      try {
        const status: any = await am.getAuctionStatus(contract, BigInt(tokId));
        const running = Array.isArray(status) ? status[0] === true : status?.isRunning === true;
        if (running) isListed = true;
      } catch {
        // Not an auction — try the 1155 straight-sale shape, then the id-keyed
        // listing getter as a last resort.
      }
      if (!isListed) {
        try {
          const s: any = await am.getERC1155SaleListing(contract, BigInt(tokId));
          isListed = Array.isArray(s) ? s[0] === true : s?.isListed === true;
        } catch { /* not a 1155 collection */ }
      }
      if (!isListed && auctionIdHint !== undefined && auctionIdHint !== null && auctionIdHint !== '') {
        const aid = Number(auctionIdHint);
        if (!Number.isNaN(aid) && aid > 0) {
          try {
            const s: any = await am.getSaleListingInfo(BigInt(aid));
            isListed = Array.isArray(s) ? s[0] === true : s?.isListed === true;
          } catch { /* not that id either */ }
        }
      }
      if (dead) return;
      setListed(isListed);
      setResolved(true);
      setLoading(false);
    })();

    return () => { dead = true; };
  }, [contract, tokId, readChainId, String(auctionIdHint ?? ""), am.getOwnerOf]);

  // Derive the label. Compare case-insensitively: the contract returns a
  // checksummed address, the wallet returns lowercase on some providers.
  const role: TokenRole = React.useMemo(() => {
    if (!resolved) return "unknown";
    if (!owner) return "none";
    const isOwner = Boolean(account) && owner.toLowerCase() === String(account).toLowerCase();
    if (isOwner) return listed ? "manage" : "sell";
    return listed ? "buy" : "none";
  }, [resolved, owner, listed, account]);

  return { role, owner, loading };
}

export default useTokenRole;
